import { Application, Container, Graphics, Text } from 'pixi.js';
import type { Point } from '../core/math';
import type { Faction } from '../data/types';
import { sectors } from '../data/content';
import type { Entity, GameSnapshot } from '../simulation/game';
import { formationSlots } from '../core/squad';

const colors: Record<Faction, number> = { equestria: 0x8454ad, interdef: 0x62785e };
const colorblindColors: Record<Faction, number> = { equestria: 0x4f8edb, interdef: 0xe39a3b };
const mapWidth = 4096;
const mapHeight = 1600;

export interface CameraBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export class Battlefield {
  private readonly world = new Container();
  private readonly terrain = new Graphics();
  private readonly overlays = new Graphics();
  private readonly unitLayer = new Container();
  private readonly labels = new Container();
  private readonly entities = new Map<number, Graphics>();
  private scale = 0.28;
  private offset: Point = { x: 0, y: 0 };
  private drag: Point | undefined;
  private selectionStart: Point | undefined;
  private colorblindPalette = false;
  private screenShake = true;
  private strikeShake = false;
  private edgeScrolling = true;
  private scrollSpeed = 220;
  public onClick?: (world: Point, shift: boolean) => void;
  public onRightClick?: (world: Point) => void;
  public onBox?: (from: Point, to: Point, shift: boolean) => void;

  public constructor(private readonly host: HTMLElement) {}
  public readonly app = new Application();

  public async initialize(): Promise<void> {
    await this.app.init({ resizeTo: this.host, background: '#1b2822', antialias: true });
    this.host.append(this.app.canvas);
    this.app.stage.addChild(this.world);
    this.world.addChild(this.terrain, this.overlays, this.unitLayer, this.labels);
    this.drawTerrain();
    this.resetCamera();
    this.bindInput();
    new ResizeObserver(() => this.applyCamera()).observe(this.host);
  }
  public render(snapshot: GameSnapshot): void {
    this.strikeShake = snapshot.effects.some(
      (effect) => effect.kind === 'damage' && effect.remaining <= 0.15,
    );
    this.applyCamera();
    this.drawOverlay(snapshot);
    const live = new Set<number>();
    for (const entity of snapshot.entities) {
      live.add(entity.id);
      let graphic = this.entities.get(entity.id);
      if (!graphic) {
        graphic = new Graphics();
        this.entities.set(entity.id, graphic);
        this.unitLayer.addChild(graphic);
      }
      this.drawEntity(graphic, entity);
    }
    for (const [id, graphic] of this.entities)
      if (!live.has(id)) {
        graphic.destroy();
        this.entities.delete(id);
      }
  }
  public panBy(worldDx: number, worldDy: number): void {
    this.offset.x -= worldDx * this.scale;
    this.offset.y -= worldDy * this.scale;
    this.applyCamera();
  }
  public centerOn(world: Point): void {
    this.offset = {
      x: this.host.clientWidth / 2 - world.x * this.scale,
      y: this.host.clientHeight / 2 - world.y * this.scale,
    };
    this.applyCamera();
  }
  public cameraBounds(): CameraBounds {
    return {
      x: Math.max(0, -this.offset.x / this.scale),
      y: Math.max(0, -this.offset.y / this.scale),
      width: Math.min(mapWidth, this.host.clientWidth / this.scale),
      height: Math.min(mapHeight, this.host.clientHeight / this.scale),
    };
  }
  public setColorblindPalette(enabled: boolean): void {
    this.colorblindPalette = enabled;
  }
  public setScreenShake(enabled: boolean): void {
    this.screenShake = enabled;
  }
  public setEdgeScrolling(enabled: boolean, speed: number): void {
    this.edgeScrolling = enabled;
    this.scrollSpeed = Math.max(100, Math.min(420, speed));
  }
  private drawTerrain(): void {
    this.terrain.clear().rect(0, 0, mapWidth, mapHeight).fill(0x27352a);
    this.terrain
      .rect(0, 680, mapWidth, 170)
      .fill(0x45413a)
      .rect(0, 730, mapWidth, 55)
      .fill(0x5d5950);
    this.terrain
      .rect(1300, 0, 115, mapHeight)
      .fill(0x4e493f)
      .rect(1360, 0, 45, mapHeight)
      .fill(0x6d6558);
    for (let x = 700; x < 3200; x += 270)
      this.terrain.rect(x, 170 + ((x * 17) % 850), 120, 80).fill(0x3c4036);
    this.terrain.rect(1830, 400, 410, 560).fill(0x343b36).stroke({ color: 0x63675d, width: 8 });
    for (let x = 0; x < mapWidth; x += 240)
      this.terrain.circle(x, 130 + ((x * 31) % 1200), 42).fill(0x2e4b31);
  }
  private drawOverlay(snapshot: GameSnapshot): void {
    this.overlays.clear();
    this.labels.removeChildren().forEach((child) => child.destroy());
    for (const sector of sectors) {
      const state = snapshot.sectors.find((entry) => entry.id === sector.id);
      if (!state) continue;
      const color = state.owner === 'neutral' ? 0x8a8a78 : this.ownerColor(state.owner);
      const radius = sector.type === 'victory' ? 44 : sector.type === 'supply' ? 32 : 25;
      this.overlays
        .circle(sector.point.x, sector.point.y, 80)
        .stroke({ color, width: 3, alpha: state.contested ? 0.9 : 0.45 });
      this.overlays
        .circle(sector.point.x, sector.point.y, radius)
        .fill({ color, alpha: 0.72 })
        .stroke({ color: 0xffffff, width: 2, alpha: 0.6 });
      if (state.progress > 0 && state.progress < 100)
        this.overlays
          .arc(
            sector.point.x,
            sector.point.y,
            radius + 8,
            -Math.PI / 2,
            -Math.PI / 2 + Math.PI * 2 * (state.progress / 100),
          )
          .stroke({ color: 0xffffff, width: 4 });
      const label = new Text({
        text:
          sector.type === 'victory' ? '★ VP' : sector.type === 'supply' ? '◆ Supply' : sector.name,
        style: {
          fill: '#eef2e6',
          fontSize: 24,
          fontFamily: 'system-ui',
          stroke: { color: '#111', width: 4 },
        },
      });
      label.anchor.set(0.5, 0);
      label.position.set(sector.point.x, sector.point.y + 92);
      this.labels.addChild(label);
    }
    for (const effect of snapshot.effects) {
      const color =
        effect.kind === 'damage'
          ? 0xe17a3e
          : effect.kind === 'recon'
            ? 0xf0d568
            : effect.kind === 'headwind'
              ? 0x69b9e7
              : 0xb9c7d3;
      this.overlays
        .circle(effect.position.x, effect.position.y, effect.radius)
        .fill({ color, alpha: effect.kind === 'smoke' ? 0.22 : 0.1 })
        .stroke({ color, width: 3, alpha: 0.8 });
      const label = new Text({
        text: effect.label,
        style: {
          fill: '#ffffff',
          fontSize: 18,
          fontFamily: 'system-ui',
          stroke: { color: '#111', width: 3 },
        },
      });
      label.anchor.set(0.5);
      label.position.set(effect.position.x, effect.position.y);
      this.labels.addChild(label);
    }
  }
  private drawEntity(graphic: Graphics, entity: Entity): void {
    const ownerColor = entity.owner === 'neutral' ? 0x353535 : this.ownerColor(entity.owner);
    graphic.clear();
    graphic.visible = !entity.embarkedIn && !entity.garrisonedIn && entity.visible !== false;
    graphic.position.set(entity.position.x, entity.position.y);
    graphic.rotation = entity.facing;
    if (entity.kind === 'wreck')
      graphic.rect(-23, -12, 46, 24).fill(0x27211d).stroke({ color: 0x9b6e3f, width: 2 });
    else if (entity.kind === 'building') {
      graphic
        .roundRect(-55, -42, 110, 84, 8)
        .fill(ownerColor)
        .stroke({ color: 0xe6dfbd, width: 3 });
      graphic.rect(-35, -16, 70, 32).fill(0x263039);
    } else if (entity.air && !entity.landed) {
      graphic
        .poly([0, -20, 26, 0, 0, 20, -26, 0])
        .fill(ownerColor)
        .stroke({ color: 0xf4edcb, width: 2 });
    } else if (entity.category === 'infantry') {
      const count = entity.members ?? 1;
      const formation = entity.formation ?? 'loose';
      const slots = formationSlots(formation, count, entity.id);
      for (let i = 0; i < slots.length; i++) {
        const slot = slots[i];
        if (!slot) continue;
        graphic.circle(slot.x, slot.y, 6).fill(ownerColor).stroke({ color: 0xe6dfbd, width: 1 });
      }
    } else {
      graphic.roundRect(-24, -13, 48, 26, 4).fill(ownerColor).stroke({ color: 0xe6dfbd, width: 2 });
      graphic.circle(4, 0, 10).fill(0x30363a);
      const turretAngle = (entity.turretFacing ?? entity.facing) - entity.facing;
      const barrelEnd = { x: 4 + Math.cos(turretAngle) * 35, y: Math.sin(turretAngle) * 35 };
      const barrelWidth = { x: -Math.sin(turretAngle) * 3, y: Math.cos(turretAngle) * 3 };
      graphic
        .poly([
          4 + barrelWidth.x,
          barrelWidth.y,
          barrelEnd.x + barrelWidth.x,
          barrelEnd.y + barrelWidth.y,
          barrelEnd.x - barrelWidth.x,
          barrelEnd.y - barrelWidth.y,
          4 - barrelWidth.x,
          -barrelWidth.y,
        ])
        .fill(0x30363a);
      if (
        entity.definitionId.includes('ambulance') ||
        entity.definitionId.includes('savior') ||
        entity.definitionId.includes('m1133')
      )
        graphic.rect(-4, -7, 8, 14).fill(0xf3eee4);
    }
    if (entity.selected)
      graphic
        .circle(0, 0, entity.kind === 'building' ? 68 : 33)
        .stroke({ color: 0xffe15c, width: 4 });
    if (entity.health < entity.maxHealth) {
      graphic.rect(-28, -38, 56, 5).fill(0x260c0c);
      graphic
        .rect(-28, -38, (56 * Math.max(0, entity.health)) / entity.maxHealth, 5)
        .fill(0x6ce38b);
    }
  }
  private bindInput(): void {
    const canvas = this.app.canvas;
    canvas.addEventListener('contextmenu', (event) => event.preventDefault());
    canvas.addEventListener('pointerdown', (event) => {
      const point = this.screen(event);
      if (event.button === 1) this.drag = point;
      else if (event.button === 0) this.selectionStart = point;
    });
    canvas.addEventListener('pointermove', (event) => {
      const point = this.screen(event);
      if (this.drag) {
        this.offset.x += point.x - this.drag.x;
        this.offset.y += point.y - this.drag.y;
        this.drag = point;
        this.applyCamera();
        return;
      }
      if (!this.edgeScrolling || event.buttons !== 0) return;
      const margin = 24;
      const dx = point.x < margin ? -1 : point.x > this.host.clientWidth - margin ? 1 : 0;
      const dy = point.y < margin ? -1 : point.y > this.host.clientHeight - margin ? 1 : 0;
      if (dx || dy) this.panBy(dx * this.scrollSpeed * 0.05, dy * this.scrollSpeed * 0.05);
    });
    canvas.addEventListener('pointerup', (event) => {
      const point = this.screen(event);
      if (event.button === 1) {
        this.drag = undefined;
        return;
      }
      if (event.button === 2) {
        this.onRightClick?.(this.toWorld(point));
        return;
      }
      if (event.button !== 0 || !this.selectionStart) return;
      const start = this.selectionStart;
      this.selectionStart = undefined;
      if (Math.hypot(point.x - start.x, point.y - start.y) > 8)
        this.onBox?.(this.toWorld(start), this.toWorld(point), event.shiftKey);
      else this.onClick?.(this.toWorld(point), event.shiftKey);
    });
    canvas.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();
        const before = this.toWorld(this.screen(event));
        this.scale = Math.max(0.16, Math.min(0.72, this.scale * (event.deltaY > 0 ? 0.9 : 1.1)));
        const after = this.toWorld(this.screen(event));
        this.offset.x += (after.x - before.x) * this.scale;
        this.offset.y += (after.y - before.y) * this.scale;
        this.applyCamera();
      },
      { passive: false },
    );
  }
  private screen(event: PointerEvent | WheelEvent): Point {
    const rect = this.app.canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }
  private toWorld(point: Point): Point {
    return { x: (point.x - this.offset.x) / this.scale, y: (point.y - this.offset.y) / this.scale };
  }
  private resetCamera(): void {
    this.offset = {
      x: this.host.clientWidth / 2 - (mapWidth / 2) * this.scale,
      y: this.host.clientHeight / 2 - (mapHeight / 2) * this.scale,
    };
    this.applyCamera();
  }
  private applyCamera(): void {
    this.clampCamera();
    this.world.scale.set(this.scale);
    this.app.canvas.dataset.cameraOffsetX = this.offset.x.toFixed(3);
    this.app.canvas.dataset.cameraOffsetY = this.offset.y.toFixed(3);
    this.app.canvas.dataset.cameraScale = this.scale.toFixed(4);
    const magnitude = this.screenShake && this.strikeShake ? 4 : 0;
    this.world.position.set(
      this.offset.x + (magnitude ? Math.random() * magnitude * 2 - magnitude : 0),
      this.offset.y + (magnitude ? Math.random() * magnitude * 2 - magnitude : 0),
    );
  }
  private clampCamera(): void {
    const clampAxis = (offset: number, viewport: number, world: number): number => {
      const scaled = world * this.scale;
      if (scaled <= viewport) return (viewport - scaled) / 2;
      return Math.min(0, Math.max(viewport - scaled, offset));
    };
    this.offset.x = clampAxis(this.offset.x, this.host.clientWidth, mapWidth);
    this.offset.y = clampAxis(this.offset.y, this.host.clientHeight, mapHeight);
  }
  private ownerColor(owner: Faction): number {
    return (this.colorblindPalette ? colorblindColors : colors)[owner];
  }
}
